import React, {useEffect, useState} from 'react';

import cs from 'classnames';
import {withRouter, WithRouterProps} from 'react-router';

import {SelectTypeFilter} from 'app/components/general/SelectTypeFilter';
import {
  applyFilter,
  DashboardFilterbar,
} from 'app/general/dashboards/DashboardFilterbar';
import {DashboardFilterbarTeamsAndRoles} from 'app/general/dashboards/DashboardFilterbarTeamsAndRoles';
import {SearchInputField} from 'app/general/widgets/SearchInputField';
import {gt, ngt} from 'app/utils/i18n';

import {TasksFilterbarAssignedUser} from './TasksFilterbarAssignedUser';
import {TasksFilterbarDueDate} from './TasksFilterbarDueDate';
import {TasksFilterbarTargetObject} from './TasksFilterbarTargetObject';

import type {TaskDashboardLocationParamsType} from 'app/tasks/types';
import type {SearchSerializedType} from 'sweet-types';

interface Props
  extends WithRouterProps<unknown, TaskDashboardLocationParamsType> {
  viewingAll: boolean;
  resetFilters: () => void;
  userTimezone: string;
}

export const TasksFilterbar = withRouter(function TasksFilterbar({
  router,
  location,
  resetFilters,
  viewingAll,
  userTimezone,
}: Props): JSX.Element {
  const [selectedTeamOrRoles, setSelectedTeamOrRoles] =
    useState<SearchSerializedType | null>(null);
  const [selectedDocSearch, setSelectedDocSearch] =
    useState<SearchSerializedType | null>(null);

  const {query} = location;
  const completedOptions = React.useMemo(
    () => [
      {
        value: '',
        text: gt('All'),
      },
      {
        value: 'true',
        text: gt('Completed'),
      },
      {
        value: 'false',
        text: gt('Not Completed'),
      },
    ],
    []
  );

  const setAssignedUser = (user: string) => {
    localApplyFilter({
      user,
    });
    return Promise.resolve();
  };

  const setCompleted = (completed: string) =>
    localApplyFilter({
      completed,
    });

  const localApplyFilter = (
    filters: Record<string, string | number | null | void>
  ): Promise<unknown> => {
    return Promise.resolve(applyFilter(router, location, filters));
  };

  useEffect(() => {
    if (query.assigner) {
      localApplyFilter({
        assigner: query.assigner,
      });
    }
  }, []);

  const onProcedureusOrProcessChange = (
    item: SearchSerializedType | null
  ): Promise<unknown> => {
    setSelectedDocSearch(item);

    localApplyFilter({
      content_type: item?.content_type || '',
      object_id: item?.object_id || '',
    });

    return Promise.resolve(true);
  };

  const onTeamOrRoleChange = (
    item: SearchSerializedType | null
  ): Promise<unknown> => {
    setSelectedTeamOrRoles(item);

    if (item == null) {
      localApplyFilter({
        role_id: '',
        team_id: '',
      });
    } else if (item.content_type === 'role') {
      localApplyFilter({
        role_id: item.object_id,
        team_id: '',
      });
    } else if (item.content_type === 'team') {
      localApplyFilter({
        team_id: item.object_id,
        role_id: '',
      });
    }

    return Promise.resolve(true);
  };

  const onSearch = (search) =>
    localApplyFilter({
      search,
    });

  const filteringByTemplate = query.template_id != null;
  const containersClass = 'col-12 col-sm-6';
  const filteringTemplateIds =
    query.template_id == null ? 0 : query.template_id.split(',').length;
  return (
    <DashboardFilterbar resetFilters={resetFilters} viewingAll={viewingAll}>
      {query.template_id != null ? (
        <div className="col-12 small mb-2">
          {ngt(
            'Results filtered to a single task series',
            'Results filtered to %s task series',
            filteringTemplateIds
          )}
        </div>
      ) : null}

      <div
        className={cs(
          'col-12 col-sm-12 col-lg-8 col-xl pr-lg-2 mb-2 mb-sm-2 mb-xl-0'
        )}
      >
        <SearchInputField
          value={query.search != null ? query.search : ''}
          onChange={onSearch}
        />
      </div>

      <div
        className={cs(
          'col-12 col-sm-12 col-lg-8 col-xl pr-lg-2 mb-2 mb-sm-2 mb-xl-0'
        )}
      >
        <TasksFilterbarDueDate
          dueSlug={query.interval}
          dueStart={query.start}
          dueEnd={query.end}
          ordering={query.ordering}
          filteringByTemplateId={filteringByTemplate}
          applyFilter={localApplyFilter}
          userTimezone={userTimezone}
        />
      </div>

      <div
        className={cs(
          containersClass,
          'col-lg-4 col-xl pr-sm-2 pr-lg-0 pr-xl-2 mb-2 mb-sm-2 mb-xl-0'
        )}
      >
        <TasksFilterbarTargetObject
          queryParams={
            query.content_type != null && query.object_id != null
              ? {
                  content_type: query.content_type,
                  object_id: query.object_id,
                }
              : undefined
          }
          selected={selectedDocSearch}
          disabled={filteringByTemplate}
          onChange={onProcedureusOrProcessChange}
        />
      </div>

      <div
        className={cs(containersClass, 'col-lg-4 col-xl pr-lg-2 mb-2 mb-lg-0')}
      >
        <TasksFilterbarAssignedUser
          disabled={filteringByTemplate}
          value={query.user}
          onChange={setAssignedUser}
        />
      </div>

      <div
        className={cs(containersClass, 'col-lg-4 col-xl pr-sm-2 mb-2 mb-sm-0')}
      >
        <DashboardFilterbarTeamsAndRoles
          placeholder={gt('Filter by role or team (assigned)...')}
          queryParams={
            query.team_id != null
              ? {
                  object_id: query.team_id,
                  content_type: 'team',
                }
              : query.role_id != null
              ? {
                  object_id: query.role_id,
                  content_type: 'role',
                }
              : null
          }
          value={selectedTeamOrRoles}
          onChange={onTeamOrRoleChange}
        />
      </div>

      <div
        className={cs(
          containersClass,
          'col-lg col-xl mb-md-0 d-flex justify-content-center'
        )}
      >
        <SelectTypeFilter
          className="form-control"
          filterValue={query.completed || ''}
          onChange={setCompleted}
          options={completedOptions}
        />
      </div>
    </DashboardFilterbar>
  );
});
